import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { apiCall } from '../AuthContext';
import { Loader2, Save, Trash2, Eye, EyeOff, AlertCircle, CheckCircle2, Megaphone } from 'lucide-react';

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'product-form', label: 'Product Form' },
  { id: 'bulk-upload', label: 'Bulk Upload' },
  { id: 'product-list', label: 'Products' },
  { id: 'batch-management', label: 'Batches' },
  { id: 'configuration', label: 'Integrations' },
  { id: 'label-designer', label: 'Label Designer' },
  { id: 'rewards', label: 'Refer & Earn' },
  { id: 'learning-videos', label: 'Learning Videos' },
];

const VARIANTS = [
  { id: 'info', label: 'Info', cls: 'bg-blue-50 border-blue-200 text-blue-800' },
  { id: 'warning', label: 'Warning', cls: 'bg-amber-50 border-amber-200 text-amber-900' },
  { id: 'success', label: 'Success', cls: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  { id: 'promo', label: 'Promo', cls: 'bg-purple-50 border-purple-200 text-purple-900' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

export default function AdminCMS() {
  const [active, setActive] = useState('dashboard');
  const [allByTab, setAllByTab] = useState({});
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [dismissible, setDismissible] = useState(true);
  const [variant, setVariant] = useState('info');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (kind, msg) => { setToast({ kind, msg }); setTimeout(() => setToast(null), 2500); };

  const loadAll = async () => {
    try {
      const res = await apiCall('/api/admin/announcements', { method: 'GET' });
      const map = {};
      (res.data?.announcements || []).forEach(a => { map[a.tab] = a; });
      setAllByTab(map);
    } catch (e) {
      showToast('error', 'Failed to load');
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Whenever active tab changes, hydrate the form fields from the loaded announcement
  useEffect(() => {
    const a = allByTab[active] || {};
    setContent(a.content_html || '');
    setTitle(a.title || '');
    setEnabled(a.enabled ?? true);
    setDismissible(a.dismissible ?? true);
    setVariant(a.variant || 'info');
  }, [active, allByTab]);

  const save = async () => {
    setBusy(true);
    try {
      await apiCall(`/api/admin/announcements/${active}`, {
        method: 'PUT',
        data: { content_html: content, enabled, dismissible, variant, title },
      });
      await loadAll();
      showToast('ok', `Announcement saved for ${active}`);
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const removeAnnouncement = async () => {
    if (!window.confirm(`Delete announcement on ${active}?`)) return;
    setBusy(true);
    try {
      await apiCall(`/api/admin/announcements/${active}`, { method: 'DELETE' });
      await loadAll();
      setContent(''); setTitle(''); setEnabled(true);
      showToast('ok', 'Deleted');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const variantCls = VARIANTS.find(v => v.id === variant)?.cls || VARIANTS[0].cls;

  return (
    <div className="space-y-6" data-testid="admin-cms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2"><Megaphone className="w-6 h-6 text-[#0052FF]" /> Announcements & Banners</h2>
          <p className="text-sm text-slate-500 mt-1">Publish a rich-text banner on top of any user-facing tab.</p>
        </div>
      </div>

      {/* Tabs row */}
      <div className="flex flex-wrap gap-2">
        {TABS.map(t => {
          const has = allByTab[t.id];
          const isOn = has?.enabled;
          return (
            <button key={t.id} onClick={() => setActive(t.id)}
              data-testid={`cms-tab-${t.id}`}
              className={`relative px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${active === t.id ? 'border-[#0052FF] bg-[#0052FF] text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-[#0052FF]/40'}`}>
              {t.label}
              {has && (
                <span className={`ml-2 inline-block w-2 h-2 rounded-full ${isOn ? 'bg-emerald-400' : 'bg-slate-400'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Editor */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Title (optional)</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Scheduled maintenance"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] focus:outline-none text-sm"
              data-testid="cms-title-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Variant</label>
            <select value={variant} onChange={e => setVariant(e.target.value)} data-testid="cms-variant-select"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] text-sm">
              {VARIANTS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Body</label>
          <div className="cms-editor border border-slate-200 rounded-lg overflow-hidden">
            <ReactQuill theme="snow" value={content} onChange={setContent} modules={QUILL_MODULES} />
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} data-testid="cms-enabled-toggle" />
            {enabled ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
            Live on user pages
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={dismissible} onChange={e => setDismissible(e.target.checked)} />
            Allow users to dismiss
          </label>
        </div>

        {content && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Preview</p>
            <div className={`border rounded-lg p-4 ${variantCls}`}>
              {title && <p className="text-sm font-bold mb-1">{title}</p>}
              <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          {allByTab[active] && (
            <button onClick={removeAnnouncement} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 disabled:opacity-50" data-testid="cms-delete-button">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#0052FF] text-white hover:bg-[#0040CC] disabled:opacity-50"
            data-testid="cms-save-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save & Publish
          </button>
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-semibold flex items-center gap-2 ${toast.kind === 'ok' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`} data-testid="cms-toast">
          {toast.kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
